import { withJsonFormsArrayControlProps } from "@jsonforms/react";
import { Button } from "pepsico-ds";
import React from "react";
import "./participation-steps-array-control.scss";

const ParticipationStepsArrayControl = ({ data, path, schema, handleChange }) => {
  // Enforce a hard cap of 5 rows; still respect schema if it is smaller than 5
  const maxItems = Math.min(schema.maxItems || Infinity, 5);
  const minItems = schema.minItems || 0;
  const canAdd = !data || data.length < maxItems;
  const canRemove = data && data.length > minItems;
  const isDefaultSingleRow =
    Array.isArray(data) &&
    data.length === 1 &&
    (!data[0]?.title || data[0]?.title === "") &&
    (!data[0]?.description || data[0]?.description === "");

  const handleAdd = () => {
    if (data && data.length >= maxItems) return; // guard against adding beyond cap
    const defaultRow = { title: "", description: "" };
    const newData = [...(data || []), defaultRow];
    handleChange(path, newData);
  };

  const handleRemove = (index) => {
    // If there is no data yet or it's a single default row, clear to empty
    if (!data || data.length === 0 || isDefaultSingleRow) {
      handleChange(path, []);
      return;
    }
    const newData = data.filter((_, i) => i !== index);
    handleChange(path, newData);
  };

  const handleFieldChange = (index, field, value) => {
    const newData = [...(data || [])];
    newData[index] = { ...newData[index], [field]: value };
    handleChange(path, newData);
  };

  // Initialize with one row if empty on mount
  React.useEffect(() => {
    if (!data || data.length === 0) {
      const defaultRow = { title: "", description: "" };
      handleChange(path, [defaultRow]);
    }
  }, [data, path, handleChange]);

  // Fallback: if data is still empty, show at least the UI for adding
  const displayData =
    data && data.length > 0 ? data : [{ title: "", description: "" }];

  return (
    <div className="participation-steps-array mt-3">
      <h3 className="psa__heading">How to participate</h3>

      {/* Header Row */}
      <div className="psa__header-row">
        <div className="psa__header-cell">TITLE</div>
        <div className="psa__header-cell">DESCRIPTION</div>
        <div className="psa__header-cell">ACTIONS</div>
      </div>

      {/* Data Rows */}
      {displayData &&
        displayData.map((item, index) => (
          <div key={index} className="psa__row">
            <input
              type="text"
              value={item.title || ""}
              onChange={(e) => handleFieldChange(index, "title", e.target.value)}
              placeholder="Title goes here"
              className="psa__input"
            />

            <input
              type="text"
              value={item.description || ""}
              onChange={(e) =>
                handleFieldChange(index, "description", e.target.value)
              }
              placeholder="Description goes here"
              className="psa__input"
            />

            <Button
              onClick={() => handleRemove(index)}
              disabled={
                !(canRemove || isDefaultSingleRow || !data || data.length === 0)
              }
              variant="tertiary"
              iconTrailing="delete"
              size="medium"
            >
              Remove
            </Button>
          </div>
        ))}

      {/* Add Step Button */}
      <Button
        onClick={handleAdd}
        disabled={!canAdd}
        size="medium"
        variant="secondary"
      >
        Add step
      </Button>

      {!canAdd && (
        <p className="psa__max-note">Maximum of {maxItems} steps allowed</p>
      )}
    </div>
  );
};

// Export the wrapped component as a named constant to satisfy React Fast Refresh
const ParticipationStepsArrayControlWrapped = withJsonFormsArrayControlProps(
  ParticipationStepsArrayControl
);
export default ParticipationStepsArrayControlWrapped;
